import React, { Component, PropTypes } from 'react';
import shouldPureComponentUpdate from 'react-pure-render/function';
import UserTouchController from 'utils/touchEvents';
import config from 'config/global';

import './Carousel.scss';

export default class Carousel extends Component {
  static propTypes = {
    images: PropTypes.object.isRequired,
    imageWidth: PropTypes.number.isRequired,
    viewWidth: PropTypes.number,
    currentItemIndex: PropTypes.number.isRequired,
    onClickNext: PropTypes.func.isRequired,
    onClickPrevious: PropTypes.func.isRequired,
    onClickThumbnail: PropTypes.func,
    toggleControlsAreActive: PropTypes.func.isRequired,
    isLastImage: PropTypes.bool.isRequired,
    isFirstImage: PropTypes.bool.isRequired,
    isDesktop: PropTypes.bool,
    isInline: PropTypes.bool,
    isSlideshow: PropTypes.bool,
    isThumbnails: PropTypes.bool,
    showCounter: PropTypes.bool,
    isRoom: PropTypes.bool,
    imageType: PropTypes.oneOf(['DGA', 'RTB', 'RGA', 'RGL', 'RGXL', 'SRC', 'INS']),
  };

  static defaultProps = {
    imageType: 'DGA',
  }

  /* istanbul ignore next */
  componentDidMount() {
    this.userTouch = new UserTouchController(this.carouselContainerElement, {
      touchstartCallback: this.onUserTouchStart,
      touchmoveCallback: this.onUserTouchMove,
      touchendCallback: this.onUserTouchEnd,
    });

    this.userTouch.init();
  }

  shouldComponentUpdate = shouldPureComponentUpdate;

  componentWillUnmount() {
    /* istanbul ignore next */
    this.userTouch.destroy();
  }

  onUserTouchStart = () => {
    this.carouselElement.style.transition = 'none';
  };

  onUserTouchMove = (event) => {
    event.preventDefault();

    const xCoordinateDifference = this.userTouch.xCoordinateLatest - this.userTouch.xCoordinateStart;
    const newTranslateValue = this.translateXValue + xCoordinateDifference;

    this.carouselElement.style.transform = this.getTransformStyleAttribute(newTranslateValue);
    this.carouselElement.style.WebkitTransform = this.getTransformStyleAttribute(newTranslateValue);
  };

  onUserTouchEnd = () => {
    this.carouselElement.style.transition = 'transform 0.5s';

    this.controlPingBack();

    if (this.userTouch.isStationary) {
      this.props.toggleControlsAreActive();
    } else if (this.userTouch.xDirection === 'left') {
      this.props.onClickNext();
    } else {
      this.props.onClickPrevious();
    }
  };

  onThumbnailClick = (event) => {
    const { isThumbnails, isInline, onClickThumbnail } = this.props;
    event.preventDefault();
    if (onClickThumbnail && isThumbnails || isInline) {
      onClickThumbnail(event);
    }
  }

  controlPingBack() {
    if (
      this.userTouch.isStationary ||
      this.props.isFirstImage && this.userTouch.xDirection === 'right' ||
      this.props.isLastImage && this.userTouch.xDirection === 'left'
    ) {
      this.carouselElement.style.transform = this.getTransformStyleAttribute(this.translateXValue);
    }
  }

  getTransformStyleAttribute(translateValue) {
    const { isInline, isSlideshow } = this.props;
    return `translate3d(${translateValue}px, ${!isInline && !isSlideshow ? '-50%' : '0'}, 0)`;
  }

  getCarouselContainerElement = (ref) => { this.carouselContainerElement = ref; };
  getCarouselElement = (ref) => { this.carouselElement = ref; };

  getSlideImageUrlByIndex(index) {
    const { images, imageType, isSlideshow } = this.props;

    const imagesArray = images.toJS();
    let imagePathname = typeof imagesArray[index] === 'string' ? imagesArray[index] : imagesArray[index].location;

    // inspirational images default to DGA if INS is unavailable
    const isImageTypeAvailable = images.getIn([`${index}`, 'sizes', imageType], false);
    const isInspirational = imageType === 'INS';
    const imageCode = isSlideshow && isInspirational && !isImageTypeAvailable ? 'DGA' : imageType;

    imagePathname = imagePathname.replace(/(^.+)(\.[A-Z]{3})(\.jpg)$/, '$1$3').replace('.jpg', `.${imageCode}.jpg`);

    const isFullURL = imagePathname.indexOf('http') === 0;

    return `${isFullURL ? '' : config.imagesBaseUrl}${imagePathname}`;
  }

  renderSlide(slideIndex) {
    const {
      currentItemIndex,
      images,
      imageWidth,
      isDesktop,
      isSlideshow,
      isThumbnails,
      showCounter,
    } = this.props;

    const slideStyles = isDesktop ? {} : {
      width: imageWidth,
    };

    if (isSlideshow || isThumbnails) {
      const imageAspectRatio = 1.86;
      slideStyles.height = isThumbnails ? imageWidth : Math.round(imageWidth / imageAspectRatio);
    }

    const imageUrl = this.getSlideImageUrlByIndex(slideIndex);

    const totalSlides = images.size;

    return (
      <li className={`carousel__list__slide ${currentItemIndex === slideIndex && 'carousel__list__slide--active'}`} key={`image-${slideIndex}`} style={slideStyles}>
        <a href="#" onClick={this.onThumbnailClick} data-index={slideIndex}>
          <img itemProp="photo" className="carousel__list__slide__image" src={imageUrl} alt="hotel photo" />
        </a>
        {showCounter && <span className="carousel__list__slide__status">[{currentItemIndex + 1} / {totalSlides}]</span>}
      </li>
    );
  }

  render() {
    const { currentItemIndex, imageWidth, images, isDesktop, isInline, isSlideshow, isThumbnails } = this.props;

    this.translateXValue = imageWidth * currentItemIndex * -1;

    const totalSlides = images.size;

    const carouselStyles = isInline ? {
      width: `${imageWidth}px`,
    } : {};

    if (isSlideshow) {
      carouselStyles.width = imageWidth;
    }

    const carouselListStyles = isDesktop || isSlideshow || isThumbnails ? {} : {
      WebkitTransform: this.getTransformStyleAttribute(this.translateXValue),
      transform: this.getTransformStyleAttribute(this.translateXValue),
      width: imageWidth * totalSlides,
    };

    if (isSlideshow) {
      carouselListStyles.width = imageWidth * totalSlides;
    }

    if (isThumbnails) {
      carouselListStyles.width = (imageWidth + 9) * (totalSlides + 1);
    }

    let className;

    if (isDesktop) {
      className = 'carousel--desktop';
    } else if (isInline) {
      className = 'carousel--inline';
    } else if (isSlideshow) {
      className = 'carousel--slideshow';
    } else if (isThumbnails) {
      className = 'carousel--thumbnails';
    } else {
      className = 'carousel--mobile';
    }

    return (
      <div className={`carousel ${className}`} ref={this.getCarouselContainerElement} style={carouselStyles}>
        <ul className="carousel__list" style={carouselListStyles} ref={this.getCarouselElement}>
          { images.map((img, index) => this.renderSlide(index)) }
        </ul>
      </div>
    );
  }

}



/** WEBPACK FOOTER **
 ** ./src/components/Carousel/Carousel.jsx
 **/